import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";

// Student Pages
import StudentDashboard from "./pages/student/StudentDashboard";
import StudentCourses from "./pages/student/StudentCourses";
import CourseDetail from "./pages/student/CourseDetail";
import StudentMyCourses, { StudentPayments } from "./pages/student/StudentMyCourses";
import LiveClassRoom from "./pages/student/LiveClassRoom";
import CertificatePage from "./pages/student/CertificatePage";
import PaymentReturn from "./pages/student/PaymentReturn";

// Teacher Pages
import TeacherDashboard from "./pages/teacher/TeacherDashboard";
import TeacherCourses from "./pages/teacher/TeacherCourses";
import TeacherClasses from "./pages/teacher/TeacherClasses";
import TeacherGrades from "./pages/teacher/TeacherGrades";
import TeacherGradesList from "./pages/teacher/TeacherGradesList";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminEnrollments from "./pages/admin/AdminEnrollments";
import ManualPage from "./pages/ManualPage";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'teacher') return <Navigate to="/teacher" replace />;
    return <Navigate to="/student" replace />;
  }

  return children;
}

// Public Route (redirect if authenticated)
function PublicRoute({ children }) {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    if (user?.role === 'teacher') return <Navigate to="/teacher" replace />;
    return <Navigate to="/student" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/manual" element={<ManualPage />} />

      {/* Student Routes */}
      <Route path="/student" element={<ProtectedRoute allowedRoles={['student', 'admin']}><StudentDashboard /></ProtectedRoute>} />
      <Route path="/student/courses" element={<ProtectedRoute allowedRoles={['student', 'admin']}><StudentCourses /></ProtectedRoute>} />
      <Route path="/student/course/:courseId" element={<ProtectedRoute allowedRoles={['student', 'admin']}><CourseDetail /></ProtectedRoute>} />
      <Route path="/student/my-courses" element={<ProtectedRoute allowedRoles={['student', 'admin']}><StudentMyCourses /></ProtectedRoute>} />
      <Route path="/student/payments" element={<ProtectedRoute allowedRoles={['student', 'admin']}><StudentPayments /></ProtectedRoute>} />
      <Route path="/student/class/:classId" element={<ProtectedRoute allowedRoles={['student', 'admin']}><LiveClassRoom /></ProtectedRoute>} />
      <Route path="/payment/return" element={<ProtectedRoute allowedRoles={['student', 'admin']}><PaymentReturn /></ProtectedRoute>} />

      {/* Certificate Routes */}
      <Route path="/certificate/:certificateId" element={<CertificatePage />} />

      {/* Teacher Routes */}
      <Route path="/teacher" element={<ProtectedRoute allowedRoles={['teacher', 'admin']}><TeacherDashboard /></ProtectedRoute>} />
      <Route path="/teacher/courses" element={<ProtectedRoute allowedRoles={['teacher', 'admin']}><TeacherCourses /></ProtectedRoute>} />
      <Route path="/teacher/courses/new" element={<ProtectedRoute allowedRoles={['teacher', 'admin']}><TeacherCourses /></ProtectedRoute>} />
      <Route path="/teacher/classes" element={<ProtectedRoute allowedRoles={['teacher', 'admin']}><TeacherClasses /></ProtectedRoute>} />
      <Route path="/teacher/grades" element={<ProtectedRoute allowedRoles={['teacher', 'admin']}><TeacherGradesList /></ProtectedRoute>} />
      <Route path="/teacher/grades/:subjectId" element={<ProtectedRoute allowedRoles={['teacher', 'admin']}><TeacherGrades /></ProtectedRoute>} />
      <Route path="/teacher/class/:classId" element={<ProtectedRoute allowedRoles={['teacher', 'admin']}><LiveClassRoom /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/courses" element={<ProtectedRoute allowedRoles={['admin']}><AdminCourses /></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute allowedRoles={['admin']}><AdminPayments /></ProtectedRoute>} />
      <Route path="/admin/enrollments" element={<ProtectedRoute allowedRoles={['admin']}><AdminEnrollments /></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#FDFBF7',
              border: '1px solid #E5DFD3',
              color: '#1A1A1A',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
